// MEMBRESÍAS F1 (20-oct-2026). Vender, renovar o cambiar de plan a un cliente. Si no viene `contact`, empieza con un
// buscador (lo abre la página Membresías). El servidor decide: mismo plan vigente = RENOVAR (extiende y suma cupo);
// otro plan = la actual se cierra y nace la nueva; sin vigente = alta. Lealify NO cobra: aquí solo se registra el pago.
import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { BadgeCheck, Loader2, Search, X } from 'lucide-react';
import { membershipsApi, fechaCorta, fmtVence, fmtVigencia, type ContactMembership, type ContactMembershipFicha, type MembershipPlan } from '../../../api/membershipsApi';
import { clientsApi } from '../../../api/clientsApi';

interface Props {
  contact?: { id: string; name: string } | null;
  current?: ContactMembership | null;
  plans?: MembershipPlan[];
  onClose: () => void;
  onSaved: (ficha: ContactMembershipFicha & { action: 'sale' | 'renewal' }, contact: { id: string; name: string }) => void;
}

const hoyYmd = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};
const ymdOff = (base: string, dias: number) => {
  const [y, m, d] = base.split('-').map(Number);
  const x = new Date(Date.UTC(y, m - 1, d + dias));
  return `${x.getUTCFullYear()}-${String(x.getUTCMonth() + 1).padStart(2, '0')}-${String(x.getUTCDate()).padStart(2, '0')}`;
};
// Mismo cálculo que el servidor (lib/memberships.js finDe): inicio + meses (12 si es solo por cupo) − 1 día, con tope al mes corto.
// null meses = no caduca (20-oct-2026; antes caía a 12 meses).
const finDe = (inicio: string, meses: number | null): string | null => {
  if (!meses) return null;
  const n = Math.min(meses, 60);
  const [y, m, d] = inicio.split('-').map(Number);
  const ultimo = new Date(Date.UTC(y, m - 1 + n + 1, 0)).getUTCDate();
  const fin = new Date(Date.UTC(y, m - 1 + n, Math.min(d, ultimo)));
  const s = `${fin.getUTCFullYear()}-${String(fin.getUTCMonth() + 1).padStart(2, '0')}-${String(fin.getUTCDate()).padStart(2, '0')}`;
  return Math.min(d, ultimo) < d ? s : ymdOff(s, -1);
};
const money = (n: number) => `$${n.toLocaleString('es-MX', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;

const SellMembershipModal: React.FC<Props> = ({ contact: contactProp, current: currentProp, plans: plansProp, onClose, onSaved }) => {
  const [contact, setContact] = useState(contactProp || null);
  const [current, setCurrent] = useState<ContactMembership | null>(currentProp ?? null);
  const [q, setQ] = useState('');
  const [results, setResults] = useState<{ id: string; name: string; phone: string }[]>([]);
  const [searching, setSearching] = useState(false);
  const [plans, setPlans] = useState<MembershipPlan[]>(plansProp || []);
  const [loadingPlans, setLoadingPlans] = useState(!plansProp);
  const [planId, setPlanId] = useState<number | null>(null);
  const [startsOn, setStartsOn] = useState(hoyYmd());
  const [price, setPrice] = useState<string>('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const activos = useMemo(() => plans.filter((p) => p.status === 'active'), [plans]);
  const vigente = current && current.status === 'active' ? current : null;
  const plan = activos.find((p) => p.id === planId) || null;
  const modo: 'new' | 'renew' | 'replace' = !vigente ? 'new' : plan && plan.id === vigente.plan_id ? 'renew' : 'replace';

  useEffect(() => {
    if (plansProp) return;
    membershipsApi.plans().then((r) => setPlans(r.plans)).catch(() => setPlans([])).finally(() => setLoadingPlans(false));
  }, [plansProp]);
  // Plan preseleccionado: el que ya tiene (renovar) o el primero a la venta.
  useEffect(() => {
    if (planId != null || !activos.length) return;
    const pref = vigente && activos.find((p) => p.id === vigente.plan_id);
    setPlanId((pref || activos[0]).id);
  }, [activos, planId, vigente]);
  useEffect(() => { if (plan && price === '') setPrice(plan.price != null ? String(plan.price) : ''); }, [plan, price]);

  // Buscador (solo cuando se abre desde la página, sin cliente elegido). Al elegir, se carga su membresía actual.
  useEffect(() => {
    if (contact) return;
    const t = q.trim();
    if (t.length < 2) { setResults([]); return; }
    let vivo = true;
    setSearching(true);
    const h = setTimeout(() => {
      clientsApi.list({ q: t, size: 8 })
        .then((r) => { if (vivo) setResults((r.clients || []).map((c) => ({ id: String(c.id), name: c.visitorName, phone: c.phoneNumber }))); })
        .catch(() => { if (vivo) setResults([]); })
        .finally(() => { if (vivo) setSearching(false); });
    }, 300);
    return () => { vivo = false; clearTimeout(h); };
  }, [q, contact]);
  const elegir = async (c: { id: string; name: string }) => {
    setContact(c); setPlanId(null); setPrice('');
    try { const f = await membershipsApi.forContact(c.id); setCurrent(f.current); } catch { setCurrent(null); }
  };

  const valido = !!contact && !!plan && (modo !== 'new' || /^\d{4}-\d{2}-\d{2}$/.test(startsOn)) && (price === '' || Number(price) >= 0);
  const guardar = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!valido || busy || !contact || !plan) return;
    setBusy(true); setError('');
    try {
      const r = await membershipsApi.sell(contact.id, {
        plan_id: plan.id, starts_on: modo === 'new' ? startsOn : null,
        price_paid: price === '' ? null : Number(price), payment_note: note.trim() || null,
      });
      onSaved(r, contact);
    } catch (err: any) {
      setError(err?.message || 'No se pudo guardar');
      setBusy(false);
    }
  };

  const preview = (() => {
    if (!plan) return '';
    if (modo === 'new') {
      const fin = finDe(startsOn, plan.duration_months || plan.leftover_months || null);
      if (!fin) return `Empieza el ${fechaCorta(startsOn)} con ${plan.quota_total} productos incluidos; no caduca, termina al usarlos.`;
      return `Empieza el ${fechaCorta(startsOn)} y vence el ${fechaCorta(fin)}${plan.quota_total ? ` o al usar ${plan.quota_total} productos, lo primero` : ''}.`;
    }
    if (modo === 'renew' && vigente) {
      const fin0 = plan.duration_months ? finDe(ymdOff(vigente.expires_on || hoyYmd(), 1), plan.duration_months) : (plan.leftover_months ? finDe(hoyYmd(), plan.leftover_months) : null);
      const fin = fin0 && vigente.expires_on && vigente.expires_on > fin0 ? vigente.expires_on : fin0;
      if (!fin) return `Se extiende: ahora ${fmtVence(vigente)} y pasará a no caducar${plan.quota_total ? ` · se suman ${plan.quota_total} productos (tendrá ${(vigente.quota_left || 0) + plan.quota_total})` : ''}.`;
      return `Se extiende: ahora ${fmtVence(vigente)} y pasará a vencer el ${fechaCorta(fin)}${plan.quota_total ? ` · se suman ${plan.quota_total} productos (tendrá ${(vigente.quota_left || 0) + plan.quota_total})` : ''}.`;
    }
    if (vigente) { const f = finDe(hoyYmd(), plan.duration_months || plan.leftover_months || null); return `La membresía actual (${vigente.plan_name}) se cierra hoy y ${plan.name} empieza hoy: ${f ? `vence el ${fechaCorta(f)}` : 'no caduca'}.`; }
    return '';
  })();

  return createPortal(
    <div className="modal-overlay fixed inset-0 z-50 flex items-end justify-center bg-black/50 p-0 sm:items-center sm:p-4" onClick={onClose}>
      <form onClick={(e) => e.stopPropagation()} onSubmit={guardar} data-testid="membresia-modal"
        className="modal-sheet w-full max-h-[92vh] overflow-y-auto rounded-t-2xl border border-line bg-surface p-5 shadow-2xl sm:max-w-lg sm:rounded-2xl">
        <div className="mb-1 flex items-center justify-between">
          <h2 className="flex items-center gap-2 text-lg font-bold text-content">
            <BadgeCheck size={18} className="text-emerald-600" /> {modo === 'renew' ? 'Renovar membresía' : modo === 'replace' ? 'Cambiar de plan' : 'Vender membresía'}
          </h2>
          <button type="button" onClick={onClose} aria-label="Cerrar" className="rounded-full p-1.5 text-content-3 hover:bg-surface-2"><X size={18} /></button>
        </div>

        {!contact ? (
          <div className="mb-4">
            <label className="mb-1 block text-xs font-medium text-content-2">Busca al cliente por nombre o teléfono</label>
            <div className="relative">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-content-3" />
              <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} data-testid="membresia-buscar" placeholder="Ana, 686 123…"
                className="w-full rounded-lg border border-line bg-surface-2 py-2.5 pl-9 pr-3 text-sm text-content outline-none focus:border-accent/50" />
            </div>
            {searching && <p className="mt-2 flex items-center gap-1 text-xs text-content-3"><Loader2 size={12} className="animate-spin" /> Buscando…</p>}
            {results.length > 0 && (
              <ul className="mt-2 divide-y divide-line rounded-lg border border-line">
                {results.map((r) => (
                  <li key={r.id}>
                    <button type="button" onClick={() => elegir({ id: r.id, name: r.name })}
                      className="flex w-full items-center justify-between px-3 py-2 text-left text-sm hover:bg-surface-2">
                      <span className="truncate text-content">{r.name}</span>
                      <span className="ml-2 shrink-0 text-xs text-content-3">{r.phone}</span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
            {!searching && q.trim().length >= 2 && results.length === 0 && (
              <p className="mt-2 text-xs text-content-3">Sin resultados. El cliente tiene que haberse registrado al menos una vez.</p>
            )}
          </div>
        ) : (
          <div className="mb-4 text-sm text-content-2">
            <b className="text-content">{contact.name}</b>
            {!contactProp && <button type="button" onClick={() => { setContact(null); setCurrent(null); }} className="ml-2 text-xs text-accent hover:underline">Cambiar</button>}
            {vigente && (
              <p className="mt-1 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs text-emerald-800 dark:border-emerald-500/30 dark:bg-emerald-500/10 dark:text-emerald-300" data-testid="membresia-actual">
                Tiene <b>{vigente.plan_name}</b>: {fmtVence(vigente)}{vigente.quota_total != null ? ` · le quedan ${vigente.quota_left} de ${vigente.quota_total}` : ''}.
              </p>
            )}
          </div>
        )}

        <fieldset disabled={!contact} className="disabled:opacity-50">
          <label className="mb-1 block text-xs font-medium text-content-2">Plan</label>
          {loadingPlans ? (
            <p className="mb-3 flex items-center gap-1 text-xs text-content-3"><Loader2 size={12} className="animate-spin" /> Cargando planes…</p>
          ) : activos.length === 0 ? (
            <p className="mb-3 text-sm text-content-3">No hay planes a la venta. Crea uno en Membresías.</p>
          ) : (
            <div className="mb-3 space-y-1.5">
              {activos.map((p) => (
                <label key={p.id} data-testid={`membresia-plan-${p.id}`}
                  className={`flex cursor-pointer items-start gap-2 rounded-lg border px-3 py-2 text-sm ${planId === p.id ? 'border-emerald-500 bg-emerald-500/10' : 'border-line hover:bg-surface-2'}`}>
                  <input type="radio" className="mt-1" checked={planId === p.id} onChange={() => { setPlanId(p.id); setPrice(p.price != null ? String(p.price) : ''); }} />
                  <span className="min-w-0 flex-1">
                    <span className="flex flex-wrap items-center justify-between gap-x-2">
                      <b className="text-content">{p.name}</b>
                      {p.price != null && <span className="text-xs text-content-2">{money(p.price)}</span>}
                    </span>
                    <span className="block text-xs text-content-3">{fmtVigencia(p)}{vigente && vigente.plan_id === p.id ? ' · es el que tiene' : ''}</span>
                  </span>
                </label>
              ))}
            </div>
          )}

          {modo === 'new' && (
            <>
              <label className="mb-1 block text-xs font-medium text-content-2">Empieza el</label>
              <input type="date" value={startsOn} min={ymdOff(hoyYmd(), -365)} max={ymdOff(hoyYmd(), 60)} onChange={(e) => setStartsOn(e.target.value)} data-testid="membresia-inicio"
                className="mb-3 rounded-lg border border-line bg-surface-2 px-2 py-1.5 text-sm text-content outline-none focus:border-accent/50" />
            </>
          )}

          <div className="mb-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div>
              <label className="mb-1 block text-xs font-medium text-content-2">Pagó (opcional)</label>
              <input type="number" min={0} step="0.01" value={price} onChange={(e) => setPrice(e.target.value)} data-testid="membresia-pago" placeholder="0"
                className="w-full rounded-lg border border-line bg-surface-2 px-3 py-2 text-sm text-content outline-none focus:border-accent/50" />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-content-2">Cómo pagó (opcional)</label>
              <input value={note} onChange={(e) => setNote(e.target.value)} maxLength={200} data-testid="membresia-nota" placeholder="efectivo, transferencia…"
                className="w-full rounded-lg border border-line bg-surface-2 px-3 py-2 text-sm text-content outline-none focus:border-accent/50" />
            </div>
          </div>
          {preview && <p className="mb-2 text-xs text-content-2" data-testid="membresia-preview">{preview}</p>}
          <p className="mb-3 text-[11px] text-content-3">Lealify no cobra la membresía: aquí solo registras que el cliente pagó. Los beneficios del plan se aplican en el kiosco y en caja.</p>
        </fieldset>

        {error && <p className="mb-2 text-sm text-red-600 dark:text-red-400">{error}</p>}
        <div className="flex justify-end gap-2">
          <button type="button" onClick={onClose} className="rounded-lg border border-line px-4 py-2 text-sm text-content-2 hover:bg-surface-2">Cancelar</button>
          <button type="submit" disabled={!valido || busy} data-testid="membresia-guardar"
            className="inline-flex items-center gap-1.5 rounded-lg bg-emerald-600 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-700 disabled:opacity-50">
            {busy && <Loader2 size={14} className="animate-spin" />} {modo === 'renew' ? 'Renovar' : modo === 'replace' ? 'Cambiar de plan' : 'Vender'}
          </button>
        </div>
      </form>
    </div>,
    document.body,
  );
};

export default SellMembershipModal;
